import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Globe, Sparkles, Check, ChevronLeft, ChevronRight, X } from 'lucide-react';

const CAROUSEL_STORAGE_KEY = 'panchi_carousel_seen';
const PLAN_CREATED_KEY = 'panchi_plan_created';

const LandingCarousel = ({ onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navigate = useNavigate();

  const slides = [
    {
      id: 1,
      headline: "Choose where you want to go",
      subtext: "Start with a destination — or let AI surprise you.",
      microcopy: "No long forms. No overwhelm.",
      icon: Globe,
      iconBg: "from-teal-400 to-cyan-500"
    },
    {
      id: 2,
      headline: "AI designs your trip around your travel DNA",
      subtext: "Relaxed? Explorer? Family trip? Panchi adapts instantly.",
      microcopy: "Smart nudges. Clear decisions.",
      icon: Sparkles,
      iconBg: "from-cyan-400 to-teal-500"
    },
    {
      id: 3,
      headline: "Refine. Save. Travel confidently.",
      subtext: "Tweak your plan. See alternatives. Move toward execution — stress-free.",
      microcopy: "Built for people who hate planning stress.",
      icon: Check,
      iconBg: "from-teal-500 to-emerald-500"
    }
  ];

  // Auto-advance every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev < slides.length - 1 ? prev + 1 : prev));
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') handleSkip();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.min(prev + 1, slides.length - 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  // Touch handlers for swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) nextSlide();
    if (isRightSwipe) prevSlide();
  };

  const handleSkip = () => {
    localStorage.setItem(CAROUSEL_STORAGE_KEY, 'true');
    onClose();
  };

  const handleStartPlanning = () => {
    localStorage.setItem(CAROUSEL_STORAGE_KEY, 'true');
    onClose();
    navigate('/planning');
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Frosted glass backdrop - blurs the page behind */}
      <div className="absolute inset-0 bg-white/60 backdrop-blur-md" />
      
      {/* Soft animated background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-300/40 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, -40, 0],
            y: [0, 40, 0],
            scale: [1.1, 1, 1.1]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-cyan-300/40 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: [0, 30, 0],
            y: [0, 50, 0]
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
          className="absolute top-1/2 right-1/3 w-64 h-64 bg-emerald-200/30 rounded-full blur-3xl"
        />
      </div>

      {/* Skip button */}
      <button
        onClick={handleSkip}
        className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
        data-testid="carousel-skip-btn"
      >
        <X className="w-5 h-5 text-gray-500" />
      </button>

      {/* Main content */}
      <div className="relative w-full max-w-lg mx-4 sm:mx-auto">
        <AnimatePresence mode="wait" custom={currentSlide}>
          <motion.div
            key={currentSlide}
            custom={currentSlide}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: "easeInOut" }}
            className="text-center px-4 sm:px-8"
          >
            {/* Light glassmorphism card with stronger visibility */}
            <div className="relative p-6 sm:p-10 rounded-3xl bg-white/95 backdrop-blur-xl border border-teal-100 shadow-2xl shadow-teal-500/20">
              {/* Icon */}
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="mb-6 sm:mb-8"
              >
                <div className="relative inline-flex items-center justify-center">
                  {/* Glow effect */}
                  <div className="absolute inset-0 w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-teal-400/20 to-cyan-400/20 rounded-full blur-xl" />
                  
                  {/* Icon container */}
                  <div className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br ${slides[currentSlide].iconBg} shadow-lg flex items-center justify-center`}>
                    {React.createElement(slides[currentSlide].icon, {
                      className: "w-8 h-8 sm:w-10 sm:h-10 text-white"
                    })}
                  </div>
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h2
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.4 }}
                className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 sm:mb-4 leading-tight"
              >
                {slides[currentSlide].headline}
              </motion.h2>

              {/* Subtext */}
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.4 }}
                className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed"
              >
                {slides[currentSlide].subtext}
              </motion.p>

              {/* Microcopy */}
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="text-xs sm:text-sm text-teal-600 italic"
              >
                {slides[currentSlide].microcopy}
              </motion.p>

              {/* CTA on last slide */}
              {currentSlide === slides.length - 1 && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.6, duration: 0.4 }}
                  className="mt-6 sm:mt-8 space-y-3"
                >
                  <button
                    onClick={handleStartPlanning}
                    className="w-full py-3 sm:py-4 px-6 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white font-semibold text-base sm:text-lg shadow-lg shadow-teal-500/25 transition-all duration-300 transform hover:scale-[1.02]"
                    data-testid="carousel-start-planning-btn"
                  >
                    Start Planning
                  </button>
                  <button
                    onClick={handleSkip}
                    className="w-full py-2 text-gray-400 hover:text-gray-600 text-sm transition-colors"
                    data-testid="carousel-skip-text-btn"
                  >
                    Skip
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Progress dots */}
        <div className="flex justify-center items-center gap-2 mt-6 sm:mt-8">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? 'w-8 h-2 bg-gradient-to-r from-teal-500 to-cyan-500'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
              data-testid={`carousel-dot-${index}`}
            />
          ))}
        </div>

        {/* Navigation arrows (desktop) */}
        <div className="hidden sm:flex justify-between absolute top-1/2 -translate-y-1/2 left-0 right-0 -mx-16 pointer-events-none">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-all pointer-events-auto border border-gray-100 ${
              currentSlide === 0 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
            }`}
            data-testid="carousel-prev-btn"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`p-3 rounded-full bg-white shadow-md hover:shadow-lg transition-all pointer-events-auto border border-gray-100 ${
              currentSlide === slides.length - 1 ? 'opacity-30 cursor-not-allowed' : 'opacity-100'
            }`}
            data-testid="carousel-next-btn"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Swipe hint (mobile) */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="sm:hidden text-center text-gray-400 text-xs mt-4"
        >
          Swipe to navigate
        </motion.p>
      </div>
    </motion.div>
  );
};

// Hook to check if carousel should be shown
export const useCarouselVisibility = () => {
  const [showCarousel, setShowCarousel] = useState(false);

  useEffect(() => {
    const hasSeenCarousel = localStorage.getItem(CAROUSEL_STORAGE_KEY);
    const hasCreatedPlan = localStorage.getItem(PLAN_CREATED_KEY);
    
    // Show carousel only if:
    // 1. User hasn't seen it before
    // 2. User hasn't created a plan
    if (!hasSeenCarousel && !hasCreatedPlan) {
      setShowCarousel(true);
    }
  }, []);

  const hideCarousel = () => {
    setShowCarousel(false);
  };

  return { showCarousel, hideCarousel };
};

// Function to mark plan as created (call this when user creates a plan)
export const markPlanCreated = () => {
  localStorage.setItem(PLAN_CREATED_KEY, 'true');
  localStorage.setItem(CAROUSEL_STORAGE_KEY, 'true');
};

export default LandingCarousel;
